<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { MAX_FILES_PER_BATCH } from 'dashboard/composables/useArrowsAiDocuments';

const emit = defineEmits(['add']);
const { t } = useI18n();

const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED =
  '.pdf,.docx,.xlsx,.txt,.csv,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv,text/markdown';

const isDragging = ref(false);
const inputRef = ref(null);

const isAccepted = file => {
  const name = file.name.toLowerCase();
  return /\.(pdf|docx|xlsx|txt|csv|md)$/.test(name);
};

const collectFiles = fileList => {
  const files = Array.from(fileList || []);
  if (files.length > MAX_FILES_PER_BATCH) {
    useAlert(t('ARROWS_AI.DOCUMENTS.TOO_MANY', { max: MAX_FILES_PER_BATCH }));
    return;
  }

  const valid = [];
  files.forEach(file => {
    if (!isAccepted(file)) {
      useAlert(t('ARROWS_AI.DOCUMENTS.INVALID'));
      return;
    }
    if (file.size > MAX_SIZE) {
      useAlert(t('ARROWS_AI.DOCUMENTS.TOO_LARGE'));
      return;
    }
    valid.push(file);
  });

  if (valid.length) emit('add', valid);
};

const onDrop = event => {
  isDragging.value = false;
  collectFiles(event.dataTransfer?.files);
};
</script>

<template>
  <button
    type="button"
    class="flex flex-col items-center justify-center w-full gap-2 px-6 py-12 text-center custom-dashed-border rounded-2xl bg-n-solid-1"
    :class="{ 'bg-n-alpha-2': isDragging }"
    @click="inputRef.click()"
    @dragenter.prevent="isDragging = true"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="onDrop"
  >
    <span class="i-lucide-upload size-6 text-n-slate-11" />
    <span class="text-sm font-medium text-n-slate-12">
      {{ t('ARROWS_AI.DOCUMENTS.DROP_TITLE') }}
    </span>
    <span class="text-sm text-n-slate-11">
      {{ t('ARROWS_AI.DOCUMENTS.DROP_SUBTITLE') }}
    </span>
    <input
      ref="inputRef"
      type="file"
      multiple
      class="hidden"
      :accept="ACCEPTED"
      @change="collectFiles($event.target.files)"
    />
  </button>
</template>
